import { buildPortalTheme, buildPreviewTheme, FONT_PRESETS } from "@/lib/tenant-theme";

/**
 * Read-only summary of a tenant's saved branding + per-portal config.
 * Shown on the Branding admin page in VIEW mode (the default), so the
 * admin sees their current settings as values — not a wall of input
 * boxes. The page's Edit button swaps this for the editable form.
 */
function Swatch({ triple, name }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="h-5 w-5 rounded border border-border" style={{ backgroundColor: `hsl(${triple})` }} />
      <span className="text-xs text-muted-foreground">{name}</span>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium text-foreground">{children}</span>
    </div>
  );
}

export default function BrandingSummary({ branding, featureConfig, portals = ["talent", "hub"] }) {
  const theme = buildPreviewTheme(branding);
  const fontLabel = FONT_PRESETS.find((f) => f.heading === branding.fonts?.heading)?.label || "Default";

  return (
    <div className="space-y-6" data-testid="branding-summary">
      <div>
        <h3 className="text-sm font-semibold">Brand identity</h3>
        <div className="mt-2 flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-md border border-border bg-background">
            {branding.logoUrl ? <img src={branding.logoUrl} alt="Logo" className="h-12 object-contain" /> : <span className="text-xs text-muted-foreground">No logo</span>}
          </div>
          <div className="flex-1">
            <Row label="Display name">{branding.shortName || <span className="italic text-muted-foreground">Not set</span>}</Row>
            <Row label="Typography">{fontLabel}</Row>
          </div>
        </div>
        <div className="mt-3">
          <p className="mb-1.5 text-xs text-muted-foreground">Shared brand palette (default for both portals)</p>
          <div className="flex flex-wrap gap-3">
            <Swatch triple={theme.colors.primary} name="Primary" />
            <Swatch triple={theme.colors.secondary} name="Secondary" />
            <Swatch triple={theme.colors.accent} name="Accent" />
            <Swatch triple={theme.colors.background} name="Background" />
          </div>
        </div>
      </div>

      {portals.filter((p) => p !== "serviqo").map((p) => {
        const cfg = featureConfig?.portals?.[p] || {};
        const pt = buildPortalTheme(branding, cfg.colors);
        const overridden = cfg.colors && Object.keys(cfg.colors).length > 0;
        return (
          <div key={p}>
            <h3 className="text-sm font-semibold capitalize">{p} portal</h3>
            <Row label="Portal name">{cfg.displayName || <span className="italic text-muted-foreground">Not set</span>}</Row>
            <Row label="Tagline">{cfg.tagline || <span className="italic text-muted-foreground">None</span>}</Row>
            <Row label="Colors">
              {overridden ? (
                <span className="flex items-center gap-2">
                  <Swatch triple={pt.colors.primary} name="" />
                  <span className="text-xs text-primary">Custom</span>
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Inherits brand</span>
              )}
            </Row>
          </div>
        );
      })}
    </div>
  );
}
